import React, { useState, useEffect } from 'react';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../firebase';
import './Home.css';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const CalendarIntegration = ({ userUID, onClose, onConnectionSuccess, initialProvider }) => {
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [selectedProvider, setSelectedProvider] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({ message: '', type: '' });
  const [existingConnections, setExistingConnections] = useState([]);

  useEffect(() => {
    loadExistingConnections();
  }, [userUID]);

  useEffect(() => {
    if (initialProvider) {
      connectCalendar(initialProvider);
    }
  }, [initialProvider]);

  const loadExistingConnections = async () => {
    try {
      const connectionsRef = ref(database, `companies/${userUID}/calendarIntegrations`);
      const snapshot = await get(connectionsRef);
      if (snapshot.exists()) {
        const connections = snapshot.val();
        setExistingConnections(Object.values(connections));
      }
    } catch (error) {
      console.error('Error loading calendar connections:', error);
    }
  };

  const connectCalendar = async (provider) => {
    setIsConnecting(true);
    setConnectionStatus({ message: '', type: '' });
    setSelectedProvider(provider);

    try {
      console.log('🔍 BACKEND_URL:', BACKEND_URL);
      console.log('🔍 Request payload:', { provider: provider, user_id: userUID });

      const response = await fetch(`${BACKEND_URL}/calendar/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: provider,
          user_id: userUID
        })
      });

      const data = await response.json();

      // DEBUG: Log the response to see what we're getting
      console.log('🔍 Calendar Connect Response:', data);
      console.log('🔍 Auth URL:', data.auth_url);

      if (data.success && data.auth_url) {
        // DEBUG: Check the exact URL being opened
        console.log('🚀 Opening popup with URL:', data.auth_url);
        console.log('🚀 URL length:', data.auth_url.length);

        // Open OAuth popup
        const popup = window.open(
          data.auth_url,
          'calendar-oauth',
          'width=500,height=600,scrollbars=yes,resizable=yes'
        );

        // Listen for popup messages
        const messageListener = (event) => {
          if (event.origin !== window.location.origin) return;

          if (event.data.type === 'CALENDAR_AUTH_SUCCESS') {
            popup.close();
            window.removeEventListener('message', messageListener);
            handleConnectionSuccess(provider, event.data.credentials);
          } else if (event.data.type === 'CALENDAR_AUTH_ERROR') {
            popup.close();
            window.removeEventListener('message', messageListener);
            setConnectionStatus({ message: t('calendarIntegration.status.error', { message: event.data.error }), type: 'error' });
            setIsConnecting(false);
          }
        };

        window.addEventListener('message', messageListener);

        // Check if popup was closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageListener);
            setIsConnecting(false);
            setConnectionStatus({ message: t('calendarIntegration.status.cancelled'), type: 'error' });
          }
        }, 1000);

      } else {
        setConnectionStatus({ message: t('calendarIntegration.status.failed', { error: data.error }), type: 'error' });
        setIsConnecting(false);
      }
    } catch (error) {
      console.error('Calendar connection error:', error);
      setConnectionStatus({ message: t('calendarIntegration.status.error', { message: error.message }), type: 'error' });
      setIsConnecting(false);
    }
  };

  const handleConnectionSuccess = async (provider, credentials) => {
    try {
      // Save connection to Firebase
      const connectionData = {
        provider: provider,
        connected_at: new Date().toISOString(),
        status: 'active',
        ...credentials
      };

      const connectionRef = ref(database, `companies/${userUID}/calendarIntegrations/${provider}`);
      await set(connectionRef, connectionData);

      setConnectionStatus({ message: t('calendarIntegration.status.success', { provider }), type: 'success' });
      setIsConnecting(false);

      // Refresh connections list
      await loadExistingConnections();

      if (onConnectionSuccess) {
        onConnectionSuccess(provider, connectionData);
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error saving connection:', error);
      setConnectionStatus({ message: t('calendarIntegration.status.error', { message: error.message }), type: 'error' });
      setIsConnecting(false);
    }
  };

  const disconnectCalendar = async (provider) => {
    if (window.confirm(t('calendarIntegration.disconnectConfirm', { provider }))) {
      try {
        const connectionRef = ref(database, `companies/${userUID}/calendarIntegrations/${provider}`);
        await set(connectionRef, null);

        setConnectionStatus({ message: t('calendarIntegration.status.disconnected', { provider }), type: 'success' });
        await loadExistingConnections();
      } catch (error) {
        console.error('Error disconnecting calendar:', error);
        setConnectionStatus({ message: t('calendarIntegration.status.failed', { error: error.message }), type: 'error' });
      }
    }
  };

  const statusTone = connectionStatus.type === 'success';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}
      >
        <div className="modal-header">
          <h2>{t('calendarIntegration.title')}</h2>
          <div className="close" onClick={onClose}>&times;</div>
        </div>

        <div className="form-section" style={{ paddingTop: '16px' }}>
          <p style={{ color: 'var(--app-heading, #031128)' }}>{t('calendarIntegration.description')}</p>


          {/* Existing Connections */}
          {existingConnections.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ color: 'var(--app-heading, #031128)', marginBottom: '12px' }}>{t('calendarIntegration.connectedCalendars')}</h3>
              {existingConnections.map((connection, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: '#F8F9FA',
                  border: '1px solid #E0E0E0',
                  borderRadius: '12px',
                  marginBottom: '10px'
                }}>
                  <div>
                    <strong style={{ textTransform: 'capitalize', color: 'var(--app-heading, #031128)' }}>{connection.provider}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {t('calendarIntegration.connectedOn', { date: new Date(connection.connected_at).toLocaleDateString() })}
                    </div>
                  </div>
                  <button
                    onClick={() => disconnectCalendar(connection.provider)}
                    className="btn-secondary"
                    style={{ fontSize: '12px', padding: '6px 10px' }}
                  >
                    {t('calendarIntegration.disconnect')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Connection Buttons */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--app-heading, #031128)', marginBottom: '12px' }}>{t('calendarIntegration.connectNew')}</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => connectCalendar('google')}
                disabled={isConnecting || existingConnections.some(c => c.provider === 'google')}
                className="btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: existingConnections.some(c => c.provider === 'google') ? '#9dbbf7' : undefined,
                  cursor: existingConnections.some(c => c.provider === 'google') ? 'not-allowed' : 'pointer',
                  opacity: isConnecting && selectedProvider === 'google' ? 0.7 : 1
                }}
              >
                <i className="fab fa-google" style={{ fontSize: '18px' }}></i>
                {existingConnections.some(c => c.provider === 'google')
                  ? t('calendarIntegration.googleConnected')
                  : isConnecting && selectedProvider === 'google'
                    ? t('calendarIntegration.connecting')
                    : t('calendarIntegration.connectGoogle')
                }
              </button>

              <button
                onClick={() => connectCalendar('outlook')}
                disabled={isConnecting || existingConnections.some(c => c.provider === 'outlook')}
                className="btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: existingConnections.some(c => c.provider === 'outlook') ? '#9dbbf7' : undefined,
                  cursor: existingConnections.some(c => c.provider === 'outlook') ? 'not-allowed' : 'pointer',
                  opacity: isConnecting && selectedProvider === 'outlook' ? 0.7 : 1
                }}
              >
                <i className="fab fa-microsoft" style={{ fontSize: '18px' }}></i>
                {existingConnections.some(c => c.provider === 'outlook')
                  ? t('calendarIntegration.outlookConnected')
                  : isConnecting && selectedProvider === 'outlook'
                    ? t('calendarIntegration.connecting')
                    : t('calendarIntegration.connectOutlook')
                }
              </button>
            </div>
          </div>

          {/* Status Messages */}
          {connectionStatus.message && (
            <div style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: statusTone ? '#d4edda' : '#f8d7da',
              color: statusTone ? 'var(--app-tone-155724, #155724)' : 'var(--app-tone-721c24, #721c24)',
              marginBottom: '20px',
              border: '1px solid #E0E0E0'
            }}>
              {connectionStatus.message}
            </div>
          )}

          {/* Information */}
          <div style={{
            backgroundColor: '#F8F9FA',
            padding: '15px',
            borderRadius: '12px',
            fontSize: '14px',
            color: '#666',
            border: '1px solid #E0E0E0'
          }}>
            <p style={{ margin: '0 0 10px 0', color: 'var(--app-heading, #031128)' }}>
              <strong>{t('calendarIntegration.infoTitle')}</strong>
            </p>
            <ul style={{ marginLeft: isRTL ? 0 : '20px', paddingLeft: isRTL ? 0 : undefined }}>
              <li>{t('calendarIntegration.info.automaticLinks')}</li>
              <li>{t('calendarIntegration.info.emailNotifications')}</li>
              <li>{t('calendarIntegration.info.workflow')}</li>
              <li>{t('calendarIntegration.info.availability')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarIntegration; 
